import type { CSSProperties } from "react";

/**
 * Paletas compartidas y helper de partición de tokens del viewer de
 * código (XML / JSON). Vive en su propio módulo para que ambos
 * highlighters (`xml-highlighter` y `json-highlighter`) las consuman
 * sin importarse entre sí.
 */
export type ColorScheme = "light" | "dark";

/**
 * Chrome del viewer · fondo, color base, color del número de línea y
 * borde lateral. Inspirado en GitHub Light / VSCode Dark+.
 */
export const CHROME = {
  light: {
    bg: "#f8fafc", // slate-50
    fg: "#1e293b", // slate-800
    lineno: "#94a3b8", // slate-400
    border: "#e2e8f0", // slate-200
  },
  dark: {
    bg: "#0f172a", // slate-900
    fg: "#e2e8f0", // slate-200
    lineno: "#475569", // slate-600
    border: "#1e293b", // slate-800
  },
} as const;

/**
 * Reparte una lista de tokens (potencialmente con `\n` en sus values)
 * en un array por línea de React nodes ya estilizados. Comparte la
 * misma lógica para XML y JSON · stateful tokenizers que cruzan saltos
 * de línea.
 *
 * Usa `key` numérico monotónico porque los tokens no tienen identidad
 * estable y se re-renderizan completos cuando cambia el texto.
 */
import type { ReactNode } from "react";

export function repartirEnLineas<T extends string>(
  tokens: { type: T; value: string }[],
  styleFn: (t: T) => CSSProperties
): ReactNode[][] {
  const lineas: ReactNode[][] = [[]];
  let claveSpan = 0;
  for (const tok of tokens) {
    const partes = tok.value.split("\n");
    for (let i = 0; i < partes.length; i++) {
      const trozo = partes[i];
      if (trozo.length > 0) {
        lineas[lineas.length - 1].push(
          // eslint-disable-next-line react/jsx-key
          <span key={claveSpan++} style={styleFn(tok.type)}>
            {trozo}
          </span>
        );
      }
      // Cada `\n` dentro del value abre una nueva línea
      if (i < partes.length - 1) {
        lineas.push([]);
      }
    }
  }
  return lineas;
}
